/**
 * Multi-type suggestions sub-hook for CSV import.
 *
 * Manages the state and handlers for multi-type file splitting: when a single
 * CSV file is split into multiple entity types (e.g. contacts + companies),
 * this hook owns the suggestion state, column/stage/association mappings for
 * virtual files, and the handlers for accepting/updating/clearing suggestions.
 *
 * Extracted from useCsvImport to keep multi-type logic isolated.
 */

import { useCallback, useState } from 'react';

import type {
  AssociationMapping,
  ColumnMappings,
  MultiTypeMappingSuggestion,
  StageMappings,
} from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';

import type { AssociationTypeSelections } from './csv-import-helpers';
import {
  directedPairKey,
  mergeColumnMappingsForFile,
  removeColumnMappingsByPrefix,
} from './csv-import-helpers';

export interface UseMultiTypeSuggestionsReturn {
  multiTypeSuggestions: Record<string | number, MultiTypeMappingSuggestion>;
  setMultiTypeSuggestions: React.Dispatch<
    React.SetStateAction<Record<string | number, MultiTypeMappingSuggestion>>
  >;
  associationTypeSelections: AssociationTypeSelections;
  setAssociationTypeSelections: React.Dispatch<
    React.SetStateAction<AssociationTypeSelections>
  >;
  handleMultiTypeSuggestionAccepted: (
    fileId: string | number,
    suggestion: MultiTypeMappingSuggestion
  ) => void;
  handleNewPropertyFieldTypeChange: (
    fileId: string | number,
    entityTypeName: string,
    propertyName: string,
    newFieldType: string
  ) => void;
  handleNewPropertyLabelChange: (
    fileId: string | number,
    entityTypeName: string,
    propertyName: string,
    newLabel: string
  ) => void;
  clearMultiTypeModeForFile: (fileId: string | number) => void;
  clearMultiTypeMode: () => void;
  /**
   * Set the chosen association type for a directed section pair (source = the
   * row above, target = the row below). Pass `ASSOCIATION_DISABLED` ('') to
   * disable the link; an absent selection defaults to the first valid type.
   */
  setAssociationTypeSelection: (
    fileId: string | number,
    sourceType: string,
    targetType: string,
    associationTypeName: string
  ) => void;
  /** Add a new entity-type section to a file's multi-type mapping. No-ops if already present. */
  addEntityTypeToFile: (
    fileId: string | number,
    entityTypeName: string
  ) => void;
}

/**
 * Manages multi-type file splitting state.
 *
 * When a CSV file can map to multiple entity types (e.g. contacts + companies),
 * the AI suggests a `MultiTypeMappingSuggestion` that splits the file into
 * virtual files. This hook owns that suggestion state and provides handlers
 * for accepting, updating, and clearing suggestions.
 *
 * Receives setters for shared state (columnMappings, stageMappings,
 * associationMappings) so it can update them when suggestions change.
 */
export function useMultiTypeSuggestions(
  setColumnMappings: React.Dispatch<React.SetStateAction<ColumnMappings>>,
  setStageMappings: React.Dispatch<React.SetStateAction<StageMappings>>,
  setAssociationMappings: React.Dispatch<
    React.SetStateAction<AssociationMapping[]>
  >
): UseMultiTypeSuggestionsReturn {
  const [multiTypeSuggestions, setMultiTypeSuggestions] = useState<
    Record<string | number, MultiTypeMappingSuggestion>
  >({});
  const [associationTypeSelections, setAssociationTypeSelections] =
    useState<AssociationTypeSelections>({});

  const handleMultiTypeSuggestionAccepted = useCallback(
    (fileId: string | number, suggestion: MultiTypeMappingSuggestion) => {
      const prefix = `${fileId}_virtual_`;
      const fileIdStr = String(fileId);
      setMultiTypeSuggestions(prev => ({ ...prev, [fileId]: suggestion }));
      // Re-accepting a suggestion clears explicit association choices for this
      // file; defaults (first valid type per directed pair) apply until the
      // user picks otherwise.
      setAssociationTypeSelections(prev => ({ ...prev, [fileId]: {} }));
      // Add/replace columnMappings for this file's virtual keys only
      setColumnMappings(prev => {
        const newEntries: ColumnMappings = {};
        for (const entityType of suggestion.entity_types) {
          newEntries[`${prefix}${entityType.entity_type_name}`] =
            entityType.mappings;
        }
        return mergeColumnMappingsForFile(prev, prefix, fileIdStr, newEntries);
      });
      // Drop orphaned stage mappings
      setStageMappings(prev => {
        const next: StageMappings = {};
        for (const [key, value] of Object.entries(prev)) {
          if (!key.startsWith(prefix) && key !== fileIdStr) {
            next[key] = value;
          }
        }
        return next;
      });
      // Drop association mappings that reference this file's old IDs
      setAssociationMappings(prev =>
        prev.filter(
          m =>
            String(m.source_file_id) !== fileIdStr &&
            String(m.target_file_id) !== fileIdStr &&
            !String(m.source_file_id).startsWith(`${fileId}_`) &&
            !String(m.target_file_id).startsWith(`${fileId}_`)
        )
      );
    },
    [setColumnMappings, setStageMappings, setAssociationMappings]
  );

  // Update field type for a new property in a multi-type suggestion
  const handleNewPropertyFieldTypeChange = useCallback(
    (
      fileId: string | number,
      entityTypeName: string,
      propertyName: string,
      newFieldType: string
    ) => {
      setMultiTypeSuggestions(prev => {
        const suggestion = prev[fileId];
        if (!suggestion) {
          return prev;
        }
        return {
          ...prev,
          [fileId]: {
            ...suggestion,
            entity_types: suggestion.entity_types.map(et => {
              if (et.entity_type_name !== entityTypeName) {
                return et;
              }
              return {
                ...et,
                new_properties: et.new_properties.map(np =>
                  np.property_name === propertyName
                    ? { ...np, field_type_name: newFieldType }
                    : np
                ),
              };
            }),
          },
        };
      });
    },
    []
  );

  // Update display label for a new property in a multi-type suggestion
  const handleNewPropertyLabelChange = useCallback(
    (
      fileId: string | number,
      entityTypeName: string,
      propertyName: string,
      newLabel: string
    ) => {
      const trimmed = newLabel.trim();
      if (trimmed === '') {
        return; // Reject empty — keep AI-suggested label.
      }
      setMultiTypeSuggestions(prev => {
        const suggestion = prev[fileId];
        if (!suggestion) {
          return prev;
        }
        return {
          ...prev,
          [fileId]: {
            ...suggestion,
            entity_types: suggestion.entity_types.map(et => {
              if (et.entity_type_name !== entityTypeName) {
                return et;
              }
              return {
                ...et,
                new_properties: et.new_properties.map(np =>
                  np.property_name === propertyName
                    ? { ...np, label: trimmed }
                    : np
                ),
              };
            }),
          },
        };
      });
    },
    []
  );

  // Clear multi-type mode for a specific file (used when a file is removed in
  // the upload step — tears down that file's virtual sections and any state
  // keyed to them so no orphaned mappings/associations survive).
  const clearMultiTypeModeForFile = useCallback(
    (fileId: string | number) => {
      logger.debug('[CSV] Clearing multi-type state for file', { fileId });
      setMultiTypeSuggestions(prev => {
        const { [fileId]: _removed, ...rest } = prev;
        return rest;
      });
      setAssociationTypeSelections(prev => {
        const { [fileId]: _removed, ...rest } = prev;
        return rest;
      });
      // Remove this file's virtual column/stage mappings
      const prefix = `${fileId}_virtual_`;
      setColumnMappings(prev => removeColumnMappingsByPrefix(prev, prefix));
      setStageMappings(prev => {
        const next: StageMappings = {};
        for (const [key, value] of Object.entries(prev)) {
          if (!key.startsWith(prefix)) {
            next[key] = value;
          }
        }
        return next;
      });
      // Remove association mappings that reference this file's virtual IDs
      setAssociationMappings(prev => {
        const filtered = prev.filter(
          m =>
            !String(m.source_file_id).startsWith(`${fileId}_`) &&
            !String(m.target_file_id).startsWith(`${fileId}_`)
        );
        if (filtered.length !== prev.length) {
          logger.debug('[CSV] Cleaned orphaned association mappings', {
            fileId,
            removedCount: prev.length - filtered.length,
          });
        }
        return filtered;
      });
    },
    [setColumnMappings, setStageMappings, setAssociationMappings]
  );

  // Clear all multi-type mode (used by reset)
  const clearMultiTypeMode = useCallback(() => {
    setMultiTypeSuggestions({});
    setAssociationTypeSelections({});
    setColumnMappings({});
    setAssociationMappings([]);
    setStageMappings({});
  }, [setColumnMappings, setAssociationMappings, setStageMappings]);

  const setAssociationTypeSelection = useCallback(
    (
      fileId: string | number,
      sourceType: string,
      targetType: string,
      associationTypeName: string
    ) => {
      setAssociationTypeSelections(prev => ({
        ...prev,
        [fileId]: {
          ...(prev[fileId] ?? {}),
          [directedPairKey(sourceType, targetType)]: associationTypeName,
        },
      }));
    },
    []
  );

  // Add a new entity-type section. DEFENSIVE: no-op if the type already exists.
  const addEntityTypeToFile = useCallback(
    (fileId: string | number, entityTypeName: string) => {
      setMultiTypeSuggestions(prev => {
        const suggestion = prev[fileId];
        if (!suggestion) {
          return prev;
        }
        if (
          suggestion.entity_types.some(
            et => et.entity_type_name === entityTypeName
          )
        ) {
          return prev;
        }
        return {
          ...prev,
          [fileId]: {
            ...suggestion,
            entity_types: [
              ...suggestion.entity_types,
              {
                entity_type_name: entityTypeName,
                mappings: {},
                new_properties: [],
              },
            ],
          },
        };
      });
      // Init an empty columnMappings entry so the section persists as a deliberate
      // drop target even with no columns. Preserve any existing entry so a no-op
      // re-add (entity type already present) doesn't wipe configured mappings.
      const editKey = `${fileId}_virtual_${entityTypeName}`;
      setColumnMappings(prev =>
        prev[editKey] ? prev : { ...prev, [editKey]: {} }
      );
    },
    [setColumnMappings]
  );

  return {
    multiTypeSuggestions,
    setMultiTypeSuggestions,
    associationTypeSelections,
    setAssociationTypeSelections,
    handleMultiTypeSuggestionAccepted,
    handleNewPropertyFieldTypeChange,
    handleNewPropertyLabelChange,
    clearMultiTypeModeForFile,
    clearMultiTypeMode,
    setAssociationTypeSelection,
    addEntityTypeToFile,
  };
}
